import { useCallback, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import * as TutorReducer from 'src/app/reducers/room/tutor.reducer';
import { addSocketListener, emitSocketEvent, removeSocketListener, socket } from 'src/common/sockets';
import {
  type GetQuestionHintData,
  type ReadTutorMessages,
  type SendAskEchoMessage,
  type SendTutorMessage,
  type SendTutorMessageVote,
  TutorSocketEventType,
} from 'src/features/room/components/tutor/tutor.types';

import { useTutorErrorHandling } from './use-tutor-error-handling';
import { useTutorMessages } from './use-tutor-messages';

interface UseTutorSocketProps {
  roomId?: string;
  roomCode: string;
}

export const useTutorSocketEvents = ({ roomId, roomCode }: UseTutorSocketProps) => {
  const {
    chat: { sessionId: tutorSessionId },
  } = useAppSelector((state) => state.tutor);

  const sendMessage = useCallback(
    (message: SendTutorMessage) => {
      if (tutorSessionId) {
        emitSocketEvent(TutorSocketEventType.SEND_MESSAGE, {
          tutorSessionId,
          roomId,
          ...message,
        });
      }
    },
    [roomId, tutorSessionId],
  );

  const readMessages = useCallback(
    (params?: { source?: string; sessionId?: string }) => {
      const { source = 'default', sessionId } = params ?? {};
      emitSocketEvent(TutorSocketEventType.READ_MESSAGES, {
        roomId,
        tutorSessionId: sessionId ?? tutorSessionId ?? undefined,
        source,
      });
    },
    [roomId, tutorSessionId],
  );

  const clearMessages = useCallback(() => {
    if (tutorSessionId) {
      emitSocketEvent(TutorSocketEventType.CLEAR_MESSAGES, { roomId, tutorSessionId });
    }
  }, [roomId, tutorSessionId]);

  const voteMessage = useCallback(
    (vote: SendTutorMessageVote) => {
      if (tutorSessionId) {
        emitSocketEvent(TutorSocketEventType.VOTE_MESSAGE, {
          ...vote,
          tutorSessionId,
        });
      }
    },
    [tutorSessionId],
  );

  const closeTermsOfUseBanner = useCallback(() => {
    emitSocketEvent(TutorSocketEventType.CLOSE_AI_TERMS_OF_USE_BANNER, {});
  }, []);

  const toggleNudges = useCallback(
    (state: boolean) => {
      emitSocketEvent(TutorSocketEventType.TOGGLE_NUDGES, {
        state,
        roomCode,
        tutorSessionId,
      });
    },
    [roomCode, tutorSessionId],
  );

  const updateDisplayMode = useCallback((mode: string) => {
    emitSocketEvent(TutorSocketEventType.UPDATE_DISPLAY_MODE, mode);
  }, []);

  const getQuestionHint = useCallback(
    ({ taskId, questionNo, roomType, roomDifficulty, tutorOpened }: GetQuestionHintData) => {
      emitSocketEvent(TutorSocketEventType.GET_QUESTION_HINT, {
        tutorSessionId: tutorSessionId ?? undefined,
        taskId,
        questionNo,
        roomCode,
        roomId,
        roomType,
        roomDifficulty,
        tutorOpened,
      });
    },
    [roomId, roomCode, tutorSessionId],
  );

  const sendAskEchoMessage = useCallback(
    (message: SendAskEchoMessage) => {
      emitSocketEvent(TutorSocketEventType.SEND_ASK_ECHO_MESSAGE, {
        tutorSessionId: tutorSessionId ?? undefined,
        roomId,
        message: message.message,
        additionalData: message.additionalData,
      });
    },
    [roomId, tutorSessionId],
  );

  return {
    sendMessage,
    readMessages,
    clearMessages,
    voteMessage,
    closeTermsOfUseBanner,
    toggleNudges,
    updateDisplayMode,
    getQuestionHint,
    sendAskEchoMessage,
  };
};

export const useTutorSocketConnectionInit = ({ roomId, roomCode }: UseTutorSocketProps) => {
  const dispatch = useAppDispatch();
  const {
    chat: { sessionId: tutorSessionId },
  } = useAppSelector((state) => state.tutor);

  const { sendMessage, readMessages } = useTutorSocketEvents({ roomId, roomCode });
  const { updateMessagesWithDelay: setMessages } = useTutorMessages();
  const { handleError: handleSocketErrors } = useTutorErrorHandling({
    handleRetryableAction: (originalPayload) => {
      if (tutorSessionId && originalPayload) {
        sendMessage(originalPayload);
      } else {
        readMessages({ source: 'error-retry' });
      }
    },
    handleSocketReconnect: () => {
      if (!roomId) {
        return;
      }
      emitSocketEvent('tutor-join', { roomId });
      readMessages({ source: 'error-reconnect' });
    },
  });

  const joinRoom = useCallback(() => {
    if (!roomId) {
      return;
    }
    if (!socket.connected) {
      socket.connect();
    }
    emitSocketEvent('tutor-join', { roomId });
  }, [roomId]);

  useEffect(() => {
    if (!roomId) {
      return;
    }
    if (!socket.connected) {
      socket.connect();
    }
  }, [roomId]);

  useEffect(() => {
    if (!roomId) {
      return;
    }

    const setIsLoading = (state: boolean) => {
      dispatch(TutorReducer.setIsLoading(state));
    };

    const setIsRestarting = (state: boolean) => {
      dispatch(TutorReducer.setIsRestarting(state));
    };

    const setIsChatDisabled = (state: boolean) => {
      dispatch(TutorReducer.setIsChatDisabled(state));
    };

    const setTutorSessionId = (sessionId: string) => {
      dispatch(TutorReducer.setTutorSessionId(sessionId));
    };

    const handleReceiveMessage = (data: ReadTutorMessages) => {
      setTutorSessionId(data.tutorSessionId);
      setMessages(data.messages, () => {
        setIsLoading(false);
        setIsRestarting(false);
      });
      setIsChatDisabled(data.status === 'disabled');
    };

    const handleInvalidateButtons = () => {
      dispatch(TutorReducer.invalidateButtons());
    };

    const handleReconnect = () => {
      joinRoom();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && !socket.connected) {
        socket.connect();
        handleReconnect();
      }
    };

    joinRoom();
    addSocketListener('connect', joinRoom);
    addSocketListener('reconnect', handleReconnect);
    addSocketListener(TutorSocketEventType.RECEIVE_MESSAGE, handleReceiveMessage);
    addSocketListener(TutorSocketEventType.INVALIDATE_BUTTONS, handleInvalidateButtons);
    addSocketListener(TutorSocketEventType.RECEIVE_QUESTION_HINT, () => {});

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      removeSocketListener('connect');
      removeSocketListener('reconnect');
      removeSocketListener(TutorSocketEventType.RECEIVE_MESSAGE);
      removeSocketListener(TutorSocketEventType.INVALIDATE_BUTTONS);
      removeSocketListener(TutorSocketEventType.RECEIVE_QUESTION_HINT);

      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, joinRoom, setMessages, roomId]);

  useEffect(() => {
    addSocketListener(TutorSocketEventType.ERROR, handleSocketErrors);
    return () => {
      removeSocketListener(TutorSocketEventType.ERROR);
    };
  }, [handleSocketErrors]);
};
